import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { checkIsPlatformAdmin, isFieldStaffUser } from './authRoles';
import { ensureStaffRoleInJwt } from './authStaffRoleSync';
import { fetchPanelAccess, hasPanelAccess, clearPanelAccessCache } from './panelAccess';
import { needsMfaChallenge } from './mfa';

async function resolveUser(sessionUser) {
  let user = sessionUser;
  if (!(await checkIsPlatformAdmin(supabase, user)) && !isFieldStaffUser(user)) {
    user = await ensureStaffRoleInJwt(supabase, user);
  }
  return user;
}

async function classifyUser(user) {
  if (await needsMfaChallenge()) return 'mfa_required';
  const access = await fetchPanelAccess(supabase);
  if (access.isOwner) return 'admin';
  if (hasPanelAccess(access)) return 'delegate';
  if (isFieldStaffUser(user)) return 'barista';
  return 'forbidden';
}

/**
 * Restores Auth session on refresh without racing onAuthStateChange null events.
 * Returns: loading | anon | admin | delegate | barista | mfa_required | forbidden
 */
export function useAuthGate() {
  const [kind, setKind] = useState('loading');
  const initialCheckDone = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const applySession = async (session) => {
      if (!session?.user) {
        setKind('anon');
        return;
      }
      const user = await resolveUser(session.user);
      if (cancelled) return;
      setKind(await classifyUser(user));
    };

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      initialCheckDone.current = true;
      await applySession(session);
    };

    void init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!initialCheckDone.current) return;

      if (event === 'SIGNED_OUT') {
        clearPanelAccessCache();
        setKind('anon');
        return;
      }

      if (session) {
        void applySession(session);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  return kind;
}
